import { defaultGraphJSON } from '@/scripts/defaultGraph'

import type {
  Workflow,
  WorkflowCreateDto,
  WorkflowUpdateDto
} from '../interfaces'
import type { AuthUser, NordyUser } from '../interfaces/user.interface'
import { useNordyUserStore } from '@/nordy/store/nordyUserStore'
import { isObjectId } from '../utils/isObjectId'
import { nordyAxios } from './client'

//! nordy - URL 파라미터에서 ObjectId를 안전하게 추출
function getValidObjectIdParam(params: URLSearchParams, key: string): string {
  const value = params.get(key)
  if (!value || value === 'undefined') return ''
  return isObjectId(value) ? value : ''
}

export const workflowApi = {
  hasWorkflowParameter: (): {
    parameter: string | null
    hasWorkflow: boolean
  } => {
    const searchParams = new URLSearchParams(window.location.search)
    const workflowId = getValidObjectIdParam(searchParams, 'flow')
    const imageId = getValidObjectIdParam(searchParams, 'image')
    const jobId = getValidObjectIdParam(searchParams, 'job')

    const parameter = workflowId || imageId || jobId
    return {
      parameter: parameter || null,
      hasWorkflow: !!parameter
    }
  },

  getWorkflow: async (id?: string) => {
    const searchParams = new URLSearchParams(window.location.search)
    const workflowId = getValidObjectIdParam(searchParams, 'flow')
    const imageId = getValidObjectIdParam(searchParams, 'image')
    const jobId = getValidObjectIdParam(searchParams, 'job')

    const resultId = id || workflowId || imageId || jobId

    if (!resultId) {
      return {
        workflowJson: JSON.stringify(defaultGraphJSON),
        prompt: JSON.stringify(defaultGraphJSON),
        workflowId: '',
        workflowName: '',
        workflowData: undefined,
        imageId: '',
        kind: 'temporary' as const,
        cover: null
      }
    }

    //!nordy - 유저 검증이 완료된 경우에만 workflow 복원 요청을 보낸다.
    let nordyUser: AuthUser | null
    try {
      nordyUser = await useNordyUserStore().ensureUserLoaded()
    } catch {
      return null
    }

    try {
      if (workflowId) {
        window.log('워크플로우를 불러옵니다')
        //!nordy - 그래프 락다운: 게이트 전용 복원 엔드포인트로 prompt 조회(비공개는 소유자/Admin만)
        const { data } = await nordyAxios.get<
          Omit<Workflow<NordyUser>, 'prompt'> & { prompt: string | null }
        >(`/v1/workflow/${resultId}/restore`)

        if (data.prompt) {
          const parseWorkflow = JSON.parse(data.prompt).workflow

          return {
            workflowJson: parseWorkflow,
            prompt: data.prompt,
            workflowId,
            workflowName: data.name,
            workflowData: data,
            imageId: '',
            kind: 'workflow' as const,
            cover: data.cover,
            userId: data.user?._id ?? null
          }
        }
      }

      if (imageId) {
        window.log('이미지를 불러옵니다')

        //!nordy - output-asset 그래프 락다운: 게이트 전용 복원 엔드포인트로 prompt 조회(비공개 워크플로우는 null)
        const { data } = await nordyAxios.get<{
          prompt: string | null
          userId: string | null
        }>(`/v1/output-asset/${imageId}/workflow-restore`)

        if (data.prompt) {
          const parseWorkflow = JSON.parse(data.prompt).workflow

          return {
            workflowJson: parseWorkflow,
            prompt: data.prompt,
            workflowId: '',
            workflowName: '',
            workflowData: undefined,
            imageId,
            kind: 'image' as const,
            cover: null,
            userId: data.userId
          }
        }
      }

      if (jobId) {
        if (!nordyUser) return null

        window.log('jobId를 불러옵니다')

        //!nordy - 그래프 락다운: 게이트 전용 복원 엔드포인트로 prompt 조회(비공개는 소유자/Admin만)
        const { data } = await nordyAxios.get<{
          prompt: string | null
          userId: string | null
        }>(`/v1/job/${jobId}/workflow-restore`)

        if (data.prompt) {
          const parseWorkflow = JSON.parse(data.prompt).workflow

          window.log(`parseWorkflow :`, parseWorkflow)

          return {
            workflowJson: parseWorkflow,
            prompt: data.prompt,
            workflowId: '',
            workflowName: '',
            workflowData: undefined,
            imageId: '',
            kind: 'image' as const,
            cover: null,
            userId: data.userId
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch workflow:', error)
    }

    window.log('워크플로우를 불러오지 못했습니다')
    return null
  },

  saveWorkflow: async (dto: WorkflowCreateDto) => {
    const { data } = await nordyAxios.post<Workflow<NordyUser>>(
      '/v1/workflow',
      dto
    )
    return data
  },

  updateWorkflow: async (id: string, dto: WorkflowUpdateDto) => {
    const { data } = await nordyAxios.patch<Workflow>(`/v1/workflow/${id}`, dto)
    return data
  }
}
